/**
 * "Add IndexAlpha to your home screen" — a bottom sheet on a phone (ROADMAP
 * "Add IndexAlpha to the home screen", owner pick 2026-09-30).
 *
 * Asked at a value moment, never on landing: the reader's THIRD page view of a
 * visit, and never in a visit where the welcome sheet (second page view)
 * already appeared, so two sheets never stack. A dismissal holds 60 days. The
 * menu's "Install app" row opens it on demand at any time.
 *
 * Android gets the browser's real install dialog behind our button; iOS Safari
 * gets the two steps, because it has no install event (see `installPrompt.ts`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Drawer, Typography } from '@mui/material';
import { IosShare, AddBoxOutlined } from '@mui/icons-material';
import analyticsService from '../services/analytics';
import { viewportClass } from '../reportWebVitals';
import { usePhoneViewport } from './usePhoneViewport';
import { PHONE_VIEWS_KEY } from './PhoneWelcomeSheet';
import {
  INSTALL_SHEET_VIEW, installRoute, onInstallAvailabilityChange, promptInstall,
  recentlyDismissed, rememberInstallDismissed, welcomeShownThisVisit, type InstallRoute,
} from './installPrompt';

/** Dispatched by the menu row to open the sheet on demand. */
export const OPEN_INSTALL_SHEET_EVENT = 'ia:open-install-sheet';

function pageViewsThisVisit(): number {
  try {
    return Number(sessionStorage.getItem(PHONE_VIEWS_KEY) || '0');
  } catch {
    return 0;
  }
}

function track(name: string, route: InstallRoute, extra: Record<string, unknown> = {}) {
  analyticsService.logEvent(name, { route, viewportClass: viewportClass(), ...extra });
}

const PhoneInstallSheet: React.FC = () => {
  const isPhone = usePhoneViewport();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState<InstallRoute>('none');
  const [onDemand, setOnDemand] = useState(false);

  const show = useCallback((source: 'auto' | 'menu') => {
    const r = installRoute();
    if (r === 'none') return;
    setRoute(r);
    setOnDemand(source === 'menu');
    setOpen(true);
    track('install_prompt_shown', r, { source });
  }, []);

  // The value moment: the third page view, read after the welcome sheet's own
  // count for this route has run (next tick), so effect order cannot matter.
  useEffect(() => {
    if (!isPhone) return undefined;
    const t = window.setTimeout(() => {
      if (pageViewsThisVisit() === INSTALL_SHEET_VIEW && !welcomeShownThisVisit() && !recentlyDismissed()) {
        show('auto');
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [pathname, isPhone, show]);

  useEffect(() => {
    const onOpen = () => show('menu');
    window.addEventListener(OPEN_INSTALL_SHEET_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_INSTALL_SHEET_EVENT, onOpen);
  }, [show]);

  // If the browser withdraws its install offer (installed elsewhere), close.
  useEffect(() => onInstallAvailabilityChange(() => {
    if (open && route === 'prompt' && installRoute() !== 'prompt') setOpen(false);
  }), [open, route]);

  const dismiss = () => {
    setOpen(false);
    // Asked unprompted and declined: stay away for a while. Opened from the
    // menu: the reader asked, so closing it is not a "no".
    if (!onDemand) rememberInstallDismissed();
    track('install_prompt_dismissed', route);
  };

  const install = async () => {
    const outcome = await promptInstall();
    setOpen(false);
    track(outcome === 'accepted' ? 'install_prompt_accepted' : 'install_prompt_dismissed', route, { outcome });
    if (outcome !== 'accepted' && !onDemand) rememberInstallDismissed();
  };

  const primarySx = {
    textTransform: 'none', fontWeight: 600, bgcolor: 'text.primary', color: 'background.paper',
    '&:hover': { bgcolor: 'text.secondary' },
  } as const;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={dismiss}
      PaperProps={{
        sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, px: 3, pt: 3,
              pb: 'calc(24px + env(safe-area-inset-bottom, 0px))' },
      }}
    >
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Add IndexAlpha to your home screen
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
        One tap from your home screen, full screen, without the browser bar.
      </Typography>

      {route === 'ios-steps' && (
        <Box component="ol" sx={{ pl: 0, m: 0, mb: 3, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IosShare sx={{ color: 'text.primary' }} />
            <Typography variant="body2">
              Tap <strong>Share</strong> in Safari&rsquo;s toolbar
            </Typography>
          </Box>
          <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddBoxOutlined sx={{ color: 'text.primary' }} />
            <Typography variant="body2">
              Choose <strong>Add to Home Screen</strong>
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {route === 'prompt' ? (
          <>
            <Button variant="contained" onClick={install} sx={primarySx}>Install</Button>
            <Button onClick={dismiss} sx={{ textTransform: 'none', color: 'text.primary' }}>Not now</Button>
          </>
        ) : (
          <Button variant="contained" onClick={dismiss} sx={primarySx}>Got it</Button>
        )}
      </Box>
    </Drawer>
  );
};

export default PhoneInstallSheet;
